import type { Node } from '@tiptap/pm/model';
import { findChildren } from '@tiptap/react';
import { Plugin } from 'prosemirror-state';
import type { List as ListConfig } from '../data/.types/lists.ts';

const createMarkerCSSVariable = (colourValue: string) => {
  return `--marker-color:${colourValue};`;
};

const firstTextNodeColor = (node: Node) => {
  const textChildren = findChildren(node, (n) => n.type.name === 'text');
  const firstTextChild = textChildren[0];

  if (firstTextChild === undefined) {
    return null;
  }

  const firstMark = firstTextChild.node.marks[0];
  if (firstMark === undefined) {
    return null;
  }

  return typeof firstMark.attrs.color === 'string'
    ? firstMark.attrs.color
    : null;
};

const shouldUpdateMarkerStyle = (
  existingNodeStyle: string | null,
  markerColor: string | null,
): boolean => {
  if (markerColor === null && existingNodeStyle === null) {
    return false;
  }

  // we have a color but no styles - update needed
  if (markerColor && existingNodeStyle === null) {
    return true;
  }

  if (existingNodeStyle) {
    // check if we have a color that's not in the style
    if (markerColor) {
      return existingNodeStyle.includes(markerColor) === false;
    }
    // or check if we need to remove old marker references
    return existingNodeStyle.includes('--marker-color');
  }

  // fallback for any edge cases
  console.warn('Possible edge-case for marker color update:', {
    markerColor,
    existingNodeStyle,
  });
  return true;
};

const AutoColourMarkersPlugin = (listConfig: ListConfig) => {
  return new Plugin({
    appendTransaction: (_transactions, _oldState, newState) => {
      if (!listConfig.autoColourMarkers) return null;

      const thisTransaction = newState.tr;
      let modified = false;

      newState.doc.descendants((node, position) => {
        if (node.type.name !== 'listItem') return;

        const firstNodeColor = firstTextNodeColor(node);

        // When modifiying a node or its attributes in a transaction – e.g. with
        // setNodeAttribute or setNodeMarkup – its stored marks can be lost.
        //
        // To prevent this, we can store the current marks before modifying the
        // node, and then restore them after the modification.
        const marks =
          thisTransaction.storedMarks || newState.selection.$from.marks();
        // thisTransaction.storedMarks ensures that the first character added
        // to a new list item will have the correct color mark applied.
        //
        // newState.selection.$from.marks() ensures that each subsequent
        // character added to the list item will also have the correct color
        // mark applied.

        const currentStyle = (node.attrs?.style as string) || null;
        const markerColorVariable = firstNodeColor
          ? createMarkerCSSVariable(firstNodeColor)
          : null;

        if (
          shouldUpdateMarkerStyle(currentStyle, markerColorVariable) === false
        ) {
          return;
        }

        thisTransaction.setNodeAttribute(
          position,
          'style',
          firstNodeColor ? createMarkerCSSVariable(firstNodeColor) : null,
        );

        thisTransaction.setStoredMarks(marks);

        modified = true;
      });

      return modified ? thisTransaction : null;
    },
  });
};

export { AutoColourMarkersPlugin };
